import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, CheckCircle2, ShieldCheck, Sparkles, Target, Users } from "lucide-react";
import SpaceJetNavbar from "../components/SpaceJetNavbar";
import SpaceJetParticleField from "../components/SpaceJetParticleField";
import Reveal from "../components/Reveal";
import { ThemeProvider } from "../contexts/ThemeContext";

const LinkTrustMarketplace = () => {
  const benefits = [
    {
      icon: Users,
      title: "Managed performance support",
      description: "Work with a team that stays involved on fit, setup, and ongoing optimization.",
    },
    {
      icon: Target,
      title: "Quality offers and traffic",
      description: "Continue in a network focused on selective partnerships and traffic that aligns with campaign goals.",
    },
    {
      icon: BarChart3,
      title: "Tracking and reporting",
      description: "Keep visibility into performance with clear tracking, reporting, and campaign insight.",
    },
    {
      icon: ShieldCheck,
      title: "Growth support",
      description: "Get practical help as you move over, launch, and look for the next step in performance.",
    },
  ];

  const points = [
    "A straightforward path for LinkTrust marketplace partners moving over to SpaceJet",
    "Support for onboarding, setup, and campaign continuation",
    "A focus on quality, transparency, and long-term performance",
  ];

  return (
    <ThemeProvider>
      <div
        className="min-h-screen bg-[#050b17] text-slate-100 spacejet-texture-dots spacejet-texture-living spacejet-infinity-bg transition-colors duration-300"
        style={{
          "--spacejet-texture-dot-opacity": 0.105,
          "--spacejet-texture-grid-opacity": 0.07,
          "--spacejet-texture-ambient-opacity": 0.085,
          "--spacejet-texture-drift-duration": "50s",
          "--spacejet-texture-glow-duration": "35s",
          "--spacejet-texture-sheen-duration": "41s",
        }}
      >
        <SpaceJetNavbar />
        <SpaceJetParticleField maxParticles={42} speed={0.14} baseOpacity={0.2} />

        <section className="relative overflow-hidden py-20 md:py-24">
          <div className="absolute inset-0 spacejet-gradient-overlay" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <Reveal animation="spacejet-fade-up">
                <div className="spacejet-page-kicker mb-6">
                  <Sparkles className="h-4 w-4 text-slate-200" />
                  LinkTrust marketplace partners
                </div>
                <h1 className="spacejet-page-title text-4xl font-semibold leading-tight text-slate-100 md:text-6xl">
                  LinkTrust partners,
                  <span className="block font-light text-slate-300">welcome to SpaceJet Network.</span>
                </h1>
              </Reveal>

              <Reveal animation="spacejet-fade-up" delay={100}>
                <p className="spacejet-page-copy mb-8 max-w-3xl text-xl">
                  SpaceJet Network is working with partners from the LinkTrust marketplace to provide managed performance support, quality offers and traffic, clear tracking, and hands-on growth guidance.
                </p>
              </Reveal>

              <Reveal animation="spacejet-fade-up" delay={200}>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Link to="/signup" className="spacejet-btn-primary group inline-flex items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200">
                    Sign up / apply
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link to="/contact" className="spacejet-btn-secondary inline-flex items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200 hover:bg-white/[0.04]">
                    Contact the team
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="spacejet-surface-card rounded-2xl p-6 md:p-8">
              <div className="grid gap-4 md:grid-cols-3">
                {points.map((point, index) => (
                  <Reveal key={point} animation="spacejet-scale-in" delay={index * 100}>
                    <div className="flex items-start gap-3 text-slate-300">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" />
                      <span>{point}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative py-20">
          <div className="absolute inset-0 spacejet-texture-grid opacity-25" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal animation="spacejet-fade-up">
              <div className="mb-12 max-w-3xl">
                <div className="spacejet-page-kicker mb-4">Transition path</div>
                <h2 className="spacejet-page-title mb-4 text-3xl font-semibold text-slate-100 md:text-4xl">What you can expect at SpaceJet.</h2>
                <p className="spacejet-page-copy text-lg">
                  If you are moving from the LinkTrust marketplace, SpaceJet is set up to help you continue with a focused partnership model and support that stays close to performance.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Reveal key={benefit.title} animation="spacejet-scale-in" delay={index * 120}>
                    <div className="group spacejet-surface-card spacejet-hover-lift rounded-2xl p-7 hover:bg-slate-800/40 transition-colors duration-300">
                      <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-7 w-7 text-slate-200" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold text-slate-100">{benefit.title}</h3>
                      <p className="leading-relaxed text-slate-400">{benefit.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <Reveal animation="spacejet-fade-up">
              <div className="spacejet-page-kicker mb-4 justify-center">Ready when you are</div>
              <h2 className="spacejet-page-title mb-6 text-4xl font-semibold text-slate-100 md:text-5xl">Ready to continue with SpaceJet?</h2>
              <p className="spacejet-page-copy mb-10 text-xl">Apply through our signup flow or reach out if you want help with the move.</p>
            </Reveal>

            <Reveal animation="spacejet-fade-up" delay={120}>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Link to="/signup" className="spacejet-btn-primary group inline-flex items-center justify-center rounded-lg px-10 py-5 font-bold transition-colors duration-200">
                  Sign up / apply
                  <ArrowRight className="ml-3 h-6 w-6 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/contact" className="spacejet-btn-secondary inline-flex items-center justify-center rounded-lg px-10 py-5 font-bold transition-colors duration-200 hover:bg-white/[0.04]">
                  Contact the team
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </ThemeProvider>
  );
};

export default LinkTrustMarketplace;
